import { type MutableRefObject } from "react";
import { PanelLeft, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "~/shared/lib/utils";
import { HEADER, ICON_BASE } from "./chat-header-tokens";
import { Button } from "~/shared/ui/button";
import { ChatBreadcrumb } from "~/shared/ui/ChatBreadcrumb";
import { useSidebar } from "~/shared/ui/sidebar";

import { useChatActions } from "~/features/chat-actions";

import { buildChatBreadcrumb } from "./build-chat-breadcrumb";
import { ChatHeaderMenu } from "./ChatHeaderMenu";
import { ChatHeaderSearchBar } from "./ChatHeaderSearchBar";
import { useChatHeaderSearch } from "./useChatHeaderSearch";

interface ChatHeaderProps {
    workspaceName?: string;
    workspaceId?: string | null;
    chatTitle?: string | null;
    projectName?: string;
    projectId?: string | null;
    sceneName?: string;
    sceneId?: string | null;
    onExport?: () => void;
    exportDisabled?: boolean;
    chatId?: string | null;
    pinnedAt?: string | null;
    // Разрешено ли писать (мутировать) активный чат. Пока неизвестно/foreign —
    // hide actions в меню (см. useChatActions).
    canWrite: boolean;
    // Заполняется VirtualMessageList'ом — функция, которая знает про
    // collapsed дни и сначала разворачивает группу, потом скроллит.
    // Используется при клике на результат in-chat search.
    scrollToMessageRef?: MutableRefObject<((messageId: string) => void) | null>;
}

export function ChatHeader({
    workspaceName,
    workspaceId,
    chatTitle,
    projectName,
    projectId,
    sceneName,
    sceneId,
    onExport,
    exportDisabled,
    chatId,
    pinnedAt,
    canWrite,
    scrollToMessageRef,
}: ChatHeaderProps) {
    const { t } = useTranslation("chat");
    const { toggleSidebar } = useSidebar();
    const displayChat = chatTitle && chatTitle.length > 0 ? chatTitle : t("untitledChat");

    const search = useChatHeaderSearch(chatId, scrollToMessageRef);

    const trail = buildChatBreadcrumb({
        workspaceName,
        workspaceId,
        projectName,
        projectId,
        sceneName,
        chatLabel: displayChat,
    });

    const { actions, dialogs } = useChatActions({
        chatId: chatId ?? null,
        workspaceId: workspaceId ?? null,
        chatTitle: chatTitle ?? null,
        pinnedAt: pinnedAt ?? null,
        projectId: projectId ?? null,
        sceneId: sceneId ?? null,
        isActive: true,
        canWrite,
    });

    return (
        <>
            <header className="sticky top-0 z-20 shrink-0 border-b border-white/5 bg-zinc-950/95 backdrop-blur-sm">
                <div className={cn("flex items-center", HEADER.height, HEADER.paddingX)}>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={toggleSidebar}
                        className="mr-1 size-9 shrink-0 text-zinc-400 hover:text-zinc-200 md:hidden"
                        aria-label={t("header.openSidebar")}
                    >
                        <PanelLeft className={ICON_BASE} />
                    </Button>
                    {search.searchMode ? (
                        <ChatHeaderSearchBar
                            value={search.searchValue}
                            onChange={search.setSearchValue}
                            onClose={search.closeSearch}
                            chatId={chatId}
                            messages={search.searchMessages}
                            isFetching={search.isFetching}
                            onSelect={search.handleResultSelect}
                            containerRef={search.searchContainerRef}
                        />
                    ) : (
                        <>
                            <ChatBreadcrumb
                                trail={trail}
                                actions={actions.length > 0 ? actions : undefined}
                                untitledLabel={t("untitledChat")}
                                className="min-w-0 flex-1"
                            />

                            <div className="flex shrink-0 items-center gap-1">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={search.openSearch}
                                    className="size-9 text-zinc-500 hover:text-zinc-200"
                                    aria-label={t("header.search.open")}
                                >
                                    <Search className={ICON_BASE} />
                                </Button>
                                <ChatHeaderMenu onExport={onExport} exportDisabled={exportDisabled} />
                            </div>
                        </>
                    )}
                </div>
            </header>
            {dialogs}
        </>
    );
}
